import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import { Tabs } from 'radix-ui';
import * as React from 'react';
import { Link, useHistory, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { useUserSession } from '../../../../models/hooks/appStateHooks';
import {
	invalidateIntegrationQuery,
	useCallbackAuthorizeUrlMutation,
	useGetExternalIntegrationQuery,
	usePostExternalIntegrationConfigurationMutation,
} from '../../../../queries';
import { useDisconnectUserFromIntegrationMutation } from '../../../../queries/Integrations/useDisconnectUserFromIntegrationMutation';
import { usePostExternalIntegrationOAuthClientMutation } from '../../../../queries/Integrations/usePostExternalIntegrationOAuthClient';
import { AdminUserSessionContext } from '../../../../viewmodels/AdminViewModels';
import salesforceLogo from '../../../assets/logo_salesforce.svg';
import { Button } from '../../../components/Button';
import { DisconnectSyncUserModal } from '../../../components/integrations/DisconnectSyncUserModal';
import { LoadingSpinner } from '../../../components/LoadingSpinner';
import { DangerIcon } from '../../../components/svgs/icons/DangerIcon';
import { InProgressIcon } from '../../../components/svgs/icons/InProgressIcon';
import { SuccessCheckIcon } from '../../../components/svgs/icons/SuccessCheckIcon';
import { TextInput } from '../../../components/TextInput';
import { nameCircles } from '../../../styles/colors';
import { bs } from '../../../styles/styles';
import { styleSheet as integrationsStyleSheet } from '../styles';
import { styleSheet } from './styles';

enum TabValues {
	Configure = 'configure',
	Connect = 'connect',
	Manage = 'manage',
}

enum IntegrationState {
	NotConfigured = 'not-configured',
	NotConnected = 'not-connected',
	Connected = 'connected',
}

export function Salesforce() {
	const history = useHistory();
	const userSession = useUserSession();
	const params = useParams<{ tab: TabValues }>();
	const [isDisableModalOpen, setIsDisableModalOpen] = React.useState(false);
	const [crmDomain, setCrmDomain] = React.useState<string>('');
	const adminUserSession = userSession as AdminUserSessionContext;
	const [isEditing, setIsEditing] = React.useState(false);

	const oauthClientMutation = usePostExternalIntegrationOAuthClientMutation({
		onError: error => {
			toast.error(error.systemMessage);
		},
		onSuccess: () => {
			invalidateIntegrationQuery({
				provider: Api.IntegrationProvider.Salesforce,
			});
		},
	});

	const disableIntegrationMutation = useDisconnectUserFromIntegrationMutation({
		onError: error => {
			toast.error(error.systemMessage);
		},
		onSuccess: () => {
			toast.success('Integration disabled successfully');
			invalidateIntegrationQuery({
				provider: Api.IntegrationProvider.Salesforce,
			});
			history.push('/integrations');
		},
	});

	const callbackAuthorizeUrlMutation = useCallbackAuthorizeUrlMutation({
		onError: error => {
			toast.error(error.systemMessage);
		},
		onSuccess: url => {
			window.location.replace(url);
		},
	});

	const integrationQuery = useGetExternalIntegrationQuery({
		provider: Api.IntegrationProvider.Salesforce,
	});

	const postExternalIntegrationConfigurationMutation = usePostExternalIntegrationConfigurationMutation({
		onError: error => {
			toast.error(error.systemMessage);
		},
		onSuccess: () => {
			invalidateIntegrationQuery({
				provider: Api.IntegrationProvider.Salesforce,
			});
		},
	});

	const changeTab = (value: string) => {
		history.replace({
			pathname: `/integrations/${Api.IntegrationProvider.Salesforce}/${value}`,
		});
	};

	const integrationState = React.useMemo(() => {
		const configuration = integrationQuery.data?.configuration as Api.ISalesforceIntegrationConfiguration;
		if (!configuration?.crmDomain) {
			return IntegrationState.NotConfigured;
		} else {
			const userConnections = integrationQuery.data?.configuration?.userConnections || [];
			const userConnection = userConnections.find(connection => connection.userId === userSession.user.id);

			return userConnection?.isConnected ? IntegrationState.Connected : IntegrationState.NotConnected;
		}
	}, [integrationQuery.data, userSession.user.id]);

	React.useEffect(() => {
		if (integrationQuery.data?.configuration) {
			const configuration = integrationQuery.data.configuration as Api.ISalesforceIntegrationConfiguration;
			if (configuration.crmDomain) {
				setCrmDomain(configuration.crmDomain);
			}
		}
	}, [integrationQuery.data]);

	// biome-ignore lint/correctness/useExhaustiveDependencies: changeTab will break this
	React.useEffect(() => {
		if (!integrationQuery.data) {
			return;
		}

		if (integrationState === IntegrationState.NotConfigured) {
			if (adminUserSession.isAdmin) {
				changeTab(TabValues.Configure);
			} else {
				changeTab(TabValues.Connect);
			}
		} else if (integrationState === IntegrationState.Connected) {
			changeTab(TabValues.Manage);
		} else {
			changeTab(TabValues.Connect);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [integrationQuery.data, integrationState, adminUserSession.isAdmin]);

	const configureCredentials = async () => {
		const configuration = integrationQuery.data?.configuration as Api.ISalesforceIntegrationConfiguration;
		if (crmDomain !== configuration?.crmDomain) {
			const updatedConfiguration: Partial<Api.ISalesforceIntegrationConfiguration> = {
				crmDomain,
			};
			await postExternalIntegrationConfigurationMutation.mutateAsync({
				integrationProvider: Api.IntegrationProvider.Salesforce,
				configuration: updatedConfiguration,
			});
		}

		setIsEditing(false);
	};

	const onEdit = () => {
		setIsEditing(true);
	};

	const onCancelEdit = () => {
		setIsEditing(false);
	};

	const connect = async () => {
		callbackAuthorizeUrlMutation.mutate({
			provider: Api.IntegrationProvider.Salesforce,
		});
	};

	const onDisable = () => {
		disableIntegrationMutation.mutate(Api.ExternalIntegrationType.Salesforce);
	};

	if (integrationQuery.isPending || oauthClientMutation.isPending) {
		return <LoadingSpinner type='large' className={css(bs.absoluteCenter)} />;
	}

	const hasSigninError = oauthClientMutation.error;
	const isNotConfigured = integrationState === IntegrationState.NotConfigured;
	const isConnected = integrationState === IntegrationState.Connected;

	return (
		<div className={css(styleSheet.container)}>
			<figure className={css(integrationsStyleSheet.logo, integrationsStyleSheet.headerLogo)}>
				<img src={salesforceLogo} alt='Salesforce Logo' />
			</figure>
			<p className={css(integrationsStyleSheet.bodyText, integrationsStyleSheet.description)}>
				Levitate can sync data from Salesforce, including client names, email addresses, physical addresses, matters,
				and more.
			</p>

			<Tabs.Root className={`tab-view ${css(styleSheet.tabView)}`} value={params.tab} onValueChange={changeTab}>
				<Tabs.List className={`tab-view-tabbar-tabs ${css(styleSheet.tabViewTabs)}`}>
					{adminUserSession.isAdmin ? (
						<Tabs.Trigger
							value={TabValues.Configure}
							className={`tab-view-tabbar-item ${
								params.tab === TabValues.Configure ? `tabbar-item-selected ` + css(styleSheet.tabItemSelected) : ''
							} ${css(styleSheet.tabViewItem)}`}
						>
							<SuccessCheckIcon
								fillColor={isNotConfigured ? nameCircles : undefined}
								ringColor={isNotConfigured ? '#F9F9F9' : undefined}
							/>
							<span>Configure</span>
						</Tabs.Trigger>
					) : null}

					<Tabs.Trigger
						value={TabValues.Connect}
						className={`tab-view-tabbar-item ${
							params.tab === TabValues.Connect ? `tabbar-item-selected ` + css(styleSheet.tabItemSelected) : ''
						} ${css(styleSheet.tabViewItem)}`}
					>
						<SuccessCheckIcon fillColor={isConnected ? undefined : nameCircles} ringColor='#F9F9F9' />
						<span>Connect</span>
					</Tabs.Trigger>

					<Tabs.Trigger
						value={TabValues.Manage}
						className={`tab-view-tabbar-item ${
							params.tab === TabValues.Manage ? `tabbar-item-selected ` + css(styleSheet.tabItemSelected) : ''
						} ${css(styleSheet.tabViewItem)}`}
					>
						<span>Manage</span>
					</Tabs.Trigger>
				</Tabs.List>

				<Tabs.Content value={TabValues.Configure} className='tab-view-content'>
					<div className={css(styleSheet.section)}>
						{isNotConfigured ? (
							<p className={css(integrationsStyleSheet.bodyText, integrationsStyleSheet.description)}>
								To get started, please provide your Salesforce custom subdomain. For example, if you log in to
								Salesforce at my-company.my.salesforce.com, enter my-company.
							</p>
						) : (
							<p className={css(integrationsStyleSheet.bodyText, integrationsStyleSheet.description)}>
								Your Salesforce account is configured to sync with Levitate. You can connect your user to the account.
							</p>
						)}

						{isNotConfigured || isEditing ? (
							<div>
								<div className={css(styleSheet.signInContainer)}>
									<div className={css(styleSheet.signInForm)}>
										<div className={css(styleSheet.inputWrapper)}>
											<span className={css(styleSheet.signInFormFieldLabel)}>Salesforce Custom Subdomain</span>
											<div className={css(styleSheet.signInFormField)}>
												<TextInput
													autoComplete='off'
													autoFocus={true}
													id='login-crm-domain'
													onChange={ev => setCrmDomain(ev.target.value)}
													type='text'
													value={crmDomain}
													className={css(styleSheet.textInput)}
												/>
												{hasSigninError && <DangerIcon backgroundColor='#fff' />}
											</div>
										</div>
									</div>
									<div className={css(bs.horizontalStack)}>
										<Button onClick={configureCredentials} kind='primary'>
											Save
										</Button>

										{isEditing ? (
											<Button onClick={onCancelEdit} kind='primary'>
												Cancel
											</Button>
										) : null}
									</div>
								</div>
							</div>
						) : (
							<div>
								<p className={css(integrationsStyleSheet.bodyText, integrationsStyleSheet.description)}>
									<b>Salesforce Custom Domain:</b> {crmDomain}
								</p>
								<Button onClick={onEdit} kind='primary'>
									Edit
								</Button>
							</div>
						)}
					</div>
				</Tabs.Content>

				<Tabs.Content value={TabValues.Connect} className='tab-view-content'>
					{isConnected ? (
						<div className={css(styleSheet.section)}>
							<p className={css(integrationsStyleSheet.bodyText)}>
								If you wish to disable this integration for your user, click Disconnect.
							</p>
							<Button
								kind='primary'
								onClick={ev => {
									ev.preventDefault();
									if (integrationQuery.data?.configuration?.syncUserId === userSession.user.id) {
										setIsDisableModalOpen(true);
									} else {
										onDisable();
									}
								}}
							>
								Disconnect
							</Button>
						</div>
					) : isNotConfigured ? (
						adminUserSession.isAdmin ? (
							<div className={css(styleSheet.section)}>
								<p className={css(integrationsStyleSheet.bodyText)}>
									To connect your user to the account, provide the missing fields in Configure.
								</p>
							</div>
						) : (
							<div className={css(styleSheet.section)}>
								<p className={css(integrationsStyleSheet.bodyText)}>
									To connect your user to your Salesforce account, please contact your administrator to configure your
									account.
								</p>
							</div>
						)
					) : (
						<div className={css(styleSheet.section)}>
							<div className={css(styleSheet.inProgressIconContainer)}>
								<InProgressIcon />
							</div>
							<p className={css(integrationsStyleSheet.bodyText)}>
								To connect your user to the account, click Connect.
							</p>
							<div>
								<Button kind='primary' onClick={() => connect()}>
									Connect
								</Button>
							</div>
						</div>
					)}
				</Tabs.Content>
				<Tabs.Content value={TabValues.Manage} className='tab-view-content'>
					<div className={css(styleSheet.section)}>
						<p className={css(integrationsStyleSheet.bodyText)}>
							Once you are connected, your Salesforce data will show up in the contacts tab.
						</p>
						<Link to='/people' className={css(bs.ctaButton)}>
							Contacts
						</Link>
					</div>
				</Tabs.Content>
			</Tabs.Root>

			<DisconnectSyncUserModal
				integrationDisplayName='Salesforce'
				isDisableModalOpen={isDisableModalOpen}
				onDisable={onDisable}
				setIsDisableModalOpen={setIsDisableModalOpen}
			/>
		</div>
	);
}
